import React, { useState, useRef, useEffect, useMemo, cloneElement, isValidElement } from "react";
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  useMergeRefs,
  type Placement,
} from "@floating-ui/react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

interface TooltipProps {
  children: React.ReactElement;
  content: React.ReactNode | ((close: () => void) => React.ReactNode);
  placement?: "top" | "bottom" | "left" | "right";
  className?: string;
  /** Override the floating panel's classes (background, padding, border, radius). */
  panelClassName?: string;
  maxWidth?: string;
  delay?: number;
  /** Enable click-to-toggle behavior alongside hover */
  clickToToggle?: boolean;
  /** If true, only opens on click (no hover). Implies clickToToggle behavior. */
  clickOnly?: boolean;
}

// Get intelligent fallback placements based on the specified placement
// This ensures the tooltip tries to stay on the same axis before flipping
function getFallbackPlacements(placement: string): Placement[] {
  switch (placement) {
    case "right":
      // For right placement, prefer staying on right side, then try left
      return ["right-start", "right-end", "left", "left-start", "left-end"];
    case "left":
      return ["left-start", "left-end", "right", "right-start", "right-end"];
    case "top":
      return ["top-start", "top-end", "bottom", "bottom-start", "bottom-end"];
    case "bottom":
      return ["bottom-start", "bottom-end", "top", "top-start", "top-end"];
    default:
      return ["bottom", "top", "left", "right"];
  }
}

export function Tooltip({
  children,
  content,
  placement = "top",
  className = "",
  panelClassName,
  maxWidth = "none",
  delay = 200,
  clickToToggle = false,
  clickOnly = false,
}: TooltipProps) {
  // clickOnly implies clickToToggle behavior
  const isClickEnabled = clickToToggle || clickOnly;
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const hideTimerRef = useRef<number | null>(null);
  const showTimerRef = useRef<number | null>(null);
  const referenceRef = useRef<HTMLElement | null>(null);

  // Track mount state for SSR safety
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Memoize fallback placements based on the specified placement
  const fallbackPlacements = useMemo(
    () => getFallbackPlacements(placement),
    [placement]
  );

  // Pass `open` to useFloating to enable conditional rendering optimization
  const { refs, floatingStyles, update } = useFloating({
    open: isOpen,
    placement: placement as Placement,
    strategy: "fixed",
    middleware: [
      offset(12),
      flip({
        fallbackPlacements,
        padding: 16,
        crossAxis: false,
      }),
      shift({ 
        padding: 16,
        crossAxis: true,
      }),
    ],
    // autoUpdate keeps position in sync while both elements are mounted
    whileElementsMounted: autoUpdate,
  });

  // Force position update when tooltip opens
  // This ensures the position is calculated after the floating element is mounted
  useEffect(() => {
    if (isOpen) {
      // Use requestAnimationFrame to ensure the DOM has updated before recalculating position
      const rafId = requestAnimationFrame(() => {
        update();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isOpen, update]);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      window.clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const clearShowTimer = () => {
    if (showTimerRef.current) {
      window.clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
  };

  const showTooltip = () => {
    clearHideTimer();
    clearShowTimer();
    showTimerRef.current = window.setTimeout(() => {
      setIsOpen(true);
      showTimerRef.current = null;
    }, delay);
  };

  const showTooltipImmediate = () => {
    clearShowTimer();
    clearHideTimer();
    setIsOpen(true);
  };

  const hideTooltip = (immediate = false) => {
    clearShowTimer();
    if (immediate) {
      clearHideTimer();
      setIsOpen(false);
      setIsPinned(false);
      return;
    }
    // Don't hide if pinned (unless immediate)
    if (isPinned) {
      return;
    }
    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      setIsOpen(false);
      hideTimerRef.current = null;
    }, 120);
  };

  const toggleTooltip = () => {
    if (isOpen) {
      setIsOpen(false);
      setIsPinned(false);
    } else {
      clearShowTimer();
      clearHideTimer();
      setIsOpen(true);
      setIsPinned(true);
    }
  };

  const closeTooltip = () => {
    setIsOpen(false);
    setIsPinned(false);
  };

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      clearShowTimer();
      clearHideTimer();
    };
  }, []);

  // Handle click outside to close pinned tooltip
  useEffect(() => {
    if (!isPinned || !isClickEnabled) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const floatingEl = refs.floating.current;
      const referenceEl = referenceRef.current;

      // Check if click is outside both the reference and floating elements
      if (
        floatingEl &&
        !floatingEl.contains(target) &&
        referenceEl &&
        !referenceEl.contains(target)
      ) {
        closeTooltip();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isPinned, isClickEnabled, refs.floating]);

  // Get the child's existing ref if any - must be before any conditional returns
  const childRef = isValidElement(children) ? (children as any).ref : null;
  
  // Merge our reference ref with any existing ref on the child - must be called unconditionally
  const mergedRef = useMergeRefs([
    refs.setReference,
    childRef,
    (node: HTMLElement | null) => {
      referenceRef.current = node;
    },
  ]);

  // Don't render portal on server
  if (!isMounted) {
    return children;
  }

  // Clone the child element to add our props
  const childWithProps = isValidElement(children)
    ? cloneElement(children, {
        ref: mergedRef,
        onMouseEnter: clickOnly
          ? (children.props as any).onMouseEnter
          : (e: React.MouseEvent) => {
              // Only show on hover if not pinned
              if (!isPinned) {
                showTooltip();
              }
              // Call child's original handler if exists
              (children.props as any).onMouseEnter?.(e);
            },
        onMouseLeave: clickOnly
          ? (children.props as any).onMouseLeave
          : (e: React.MouseEvent) => {
              hideTooltip(false);
              (children.props as any).onMouseLeave?.(e);
            },
        onFocus: clickOnly
          ? (children.props as any).onFocus
          : (e: React.FocusEvent) => {
              if (!isPinned) {
                showTooltipImmediate();
              }
              (children.props as any).onFocus?.(e);
            },
        onBlur: clickOnly
          ? (children.props as any).onBlur
          : (e: React.FocusEvent) => {
              hideTooltip(false);
              (children.props as any).onBlur?.(e);
            },
        onClick: isClickEnabled
          ? (e: React.MouseEvent) => {
              // Prevent default only if we're handling the click for toggle
              e.stopPropagation();
              toggleTooltip();
              // Call child's original handler if exists
              (children.props as any).onClick?.(e);
            }
          : (children.props as any).onClick,
        className: className 
          ? `${(children.props as any).className || ''} ${className}`.trim()
          : (children.props as any).className,
      } as any)
    : children;

  return (
    <>
      {childWithProps}

      {ReactDOM.createPortal(
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={refs.setFloating}
              style={{
                position: floatingStyles.position,
                top: floatingStyles.top,
                left: floatingStyles.left,
                zIndex: 99999,
                maxWidth: maxWidth,
                // Apply transform separately so Framer Motion doesn't override it
                ...((floatingStyles as any).transform ? { transform: (floatingStyles as any).transform } : {}),
              }}
              className={
                panelClassName ??
                "bg-slate-900 text-white dark:bg-slate-800 dark:text-slate-200 px-4 py-3 rounded-2xl text-sm shadow-2xl"
              }
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.12,
                ease: "easeOut",
              }}
              onMouseEnter={showTooltipImmediate}
              onMouseLeave={() => hideTooltip(false)}
            >
              {typeof content === "function" ? content(closeTooltip) : content}
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  );
}
